import { load } from '@loaders.gl/core'
import { MVTLoader } from '@loaders.gl/mvt'
import type { MVTLoaderOptions } from '@loaders.gl/mvt'
import type { _Tile2DHeader, _TileLoadProps, TileLayerProps } from '@deck.gl/geo-layers'
import { TileLayer } from '@deck.gl/geo-layers'
import type { PMTiles } from 'pmtiles'
import { SimpleMeshLayer } from '@deck.gl/mesh-layers'
import { COORDINATE_SYSTEM, type Material } from '@deck.gl/core'
import { Geometry } from '@luma.gl/engine'

const arrowMesh = new Geometry({
  topology: 'triangle-list',
  attributes: {
    positions: {
      value: new Float32Array([
        // Head (Triangle)
        0,
        1,
        0, // 0: Top Tip
        -0.5,
        0.5,
        0, // 1: Left Head
        0.5,
        0.5,
        0, // 2: Right Head
        // Tail (Rectangle)
        -0.1,
        0.5,
        0, // 3: Top Left Tail
        0.1,
        0.5,
        0, // 4: Top Right Tail
        -0.1,
        -1,
        0, // 5: Bottom Left Tail
        0.1,
        -1,
        0, // 6: Bottom Right Tail
      ]),
      size: 3,
    },
  },
  indices: {
    value: new Uint16Array([
      0,
      1,
      2, // Head triangle
      3,
      5,
      4, // Tail tri 1
      4,
      5,
      6, // Tail tri 2
    ]),
    size: 1,
  },
})

const ARROW_MATERIAL: Material = {
  ambient: 0.8,
  diffuse: 0,
  shininess: 0,
  specularColor: [0, 0, 0],
} as const

export type PMTilesLayerProps<T> = TileLayerProps<T> & {
  pmTiles: PMTiles
  wavesProperty: string
  depths: number[]
  getColor: (v: number) => [number, number, number]
}

const LOAD_OPTIONS: MVTLoaderOptions = {
  mvt: { shape: 'geojson' },
}

const cache: Record<string, unknown> = {}

export class WaveArrowsLayer<T> extends TileLayer {
  readonly getColor: (v: number) => [number, number, number]
  readonly pmTiles: PMTiles
  readonly wavesProperty: string
  readonly depths: number[]

  constructor(props: PMTilesLayerProps<T>) {
    super({ ...props, maxZoom: 14 })
    this.pmTiles = props.pmTiles
    this.wavesProperty = props.wavesProperty
    this.depths = props.depths
    this.getColor = props.getColor
  }

  async getTileData(tile: _TileLoadProps) {
    const { signal } = tile
    const { z, x, y } = tile.index
    const key = `${this.props.id}:${z}:${x}:${y}`
    if (cache[key]) {
      return cache[key]
    }

    const data = await this.pmTiles.getZxy(z, x, y, signal)
    if (!data) {
      return
    }
    if (signal?.aborted) {
      return
    }
    const result = await load(data.data, MVTLoader, LOAD_OPTIONS)
    cache[key] = result
    return result
  }

  renderSubLayers(
    props: TileLayer['props'] & {
      id: string
      data: unknown
      _offset: number
      tile: _Tile2DHeader<unknown>
    }
  ) {
    if (!props.data?.length) {
      return []
    }
    const { tile } = props
    const [minCoords, maxCoords] = tile.boundingBox
    const [west, south] = minCoords
    return new SimpleMeshLayer(props, {
      mesh: arrowMesh,
      coordinateSystem: COORDINATE_SYSTEM.LNGLAT_OFFSETS,
      coordinateOrigin: [west!, south!, 0],
      getColor: (d) => {
        const wp = d.properties.WPsum
        if (wp === undefined || wp === null || isNaN(wp)) {
          return [0, 0, 0, 0]
        }
        if (!this.depths.includes(d.properties.point_depth)) {
          // If this point's depth is not selected, then return an invisible
          // polygon, effectively hiding it.
          return [0, 0, 0, 0]
        }
        return this.getColor(wp)
      },
      getOrientation: (d) => {
        const angle = d.properties[this.wavesProperty]
        if (angle === undefined || angle === null || isNaN(angle)) {
          return [0, 0, 0]
        }
        return [0, -(angle + 180), 0]
      },
      getScale: () => {
        const zoom = tile.index.z
        const baseScale = 600
        const referenceZoom = 10
        const scaleFactor = Math.pow(1.5, referenceZoom - zoom)
        const scale = Math.max(200, Math.min(3000, baseScale * scaleFactor))
        return [scale, scale, scale]
      },
      getPosition: (d) => {
        // These coordinates are tile-relative. So (0,0) means southwest
        // corner, and (0.5,0.5) means "middle of the tile". We have to convert
        // back into latitude and longitude to render correctly.
        const [west, south] = minCoords
        const [east, north] = maxCoords
        const coords = d.geometry?.coordinates
        if (!coords) return [0, 0, 0]

        const x = coords[0] * (east! - west!)
        const y = (1 - coords[1]) * (north! - south!)
        return [x, y, 0]
      },
      material: ARROW_MATERIAL,
      updateTriggers: {
        getColor: this.depths,
      },
      refinementStrategy: 'no-overlap',
      pickable: true,
    })
  }
}
